/**
 * Stage L (module progression, docs/GAME.md#progression): the exclusive weekly challenge
 * modifiers. They exist only in challenge games (GameSettings.challenge, derived by the server)
 * and can never be switched on or off in normal games (src/shared/validate.ts rejects lobby
 * settings that carry them).
 *
 * One sentence per modifier (design rule 1) lives in the i18n section wave4:progression under
 * `challenge.mod.<id>.sentence`; the log lines under `challenge.event.<id>`.
 */
import type { ChallengeModifierId, ChallengeSpec } from "../types";

/** All modifiers in display order. */
export const CHALLENGE_MODIFIER_IDS: readonly ChallengeModifierId[] = [
  "earthquakes",
  "wildfires",
  "floods",
  "meteorShower",
  "plagueWaves",
  "goldRush",
  "doubleEconomy",
  "permanentFog",
  "shrinkingWorld",
  "lightning",
  "taxStorms",
  "leaderTax",
];

/** At most this many modifiers in one challenge. */
export const MAX_CHALLENGE_MODIFIERS = 4;

/** Week id format: ISO week "2026-W43". */
export const CHALLENGE_WEEK_RE = /^\d{4}-W\d{2}$/;

/**
 * Tunables (seconds at game speed 1). `first` = delay after the spawn phase, `every` = interval
 * between strikes (jittered by `jitter`, a fraction). Radii are a share of sqrt(map tiles),
 * clamped to [minR, maxR].
 */
export const CHALLENGE_TUNING = {
  jitter: 0.3,
  earthquakes: { first: 75, every: 140, radiusShare: 0.015, minR: 5, maxR: 16 },
  wildfires: { first: 60, every: 120, steps: 8, stepSeconds: 1, radiusShare: 0.01, minR: 3, maxR: 10 },
  floods: { first: 90, every: 150, radiusShare: 0.016, minR: 5, maxR: 18, lowland: 4 },
  meteorShower: { first: 100, every: 170, impactsMin: 3, impactsMax: 5, radius: 3 },
  plagueWaves: { first: 80, every: 130, troopLoss: 0.12, workerLoss: 0.06 },
  goldRush: { first: 45, every: 100, duration: 25, extraGold: 2 },
  doubleEconomy: { goldMult: 1, growthMult: 1 },
  shrinkingWorld: { first: 120, every: 45, stepShare: 0.02, maxShare: 0.4 },
  lightning: { first: 30, every: 20 },
  taxStorms: { first: 90, every: 150, goldLoss: 0.3 },
  leaderTax: { first: 90, every: 120, share: 0.25 },
} as const;

export function isChallengeModifier(x: unknown): x is ChallengeModifierId {
  return typeof x === "string" && (CHALLENGE_MODIFIER_IDS as readonly string[]).includes(x);
}

/**
 * Validate a challenge spec: a week id and 1..MAX_CHALLENGE_MODIFIERS known, distinct modifiers.
 * Returns the canonical form (fixed key order, modifiers in display order) or null.
 */
export function validateChallengeSpec(x: unknown): ChallengeSpec | null {
  if (typeof x !== "object" || x === null || Array.isArray(x)) return null;
  const o = x as Record<string, unknown>;
  if (typeof o.week !== "string" || !CHALLENGE_WEEK_RE.test(o.week)) return null;
  if (!Array.isArray(o.modifiers) || o.modifiers.length === 0 || o.modifiers.length > MAX_CHALLENGE_MODIFIERS) return null;
  const set = new Set<ChallengeModifierId>();
  for (const m of o.modifiers) {
    if (!isChallengeModifier(m) || set.has(m)) return null;
    set.add(m);
  }
  return { week: o.week, modifiers: CHALLENGE_MODIFIER_IDS.filter((m) => set.has(m)) };
}
